import {Injectable} from "@angular/core";
import {ValidationMsg} from "../../../umbrella-common/services/form.service";
import {FormBuilder, FormGroup, Validators} from "@angular/forms";
import {BillingInformation, ContactPerson, Order} from "../../../store/state/order.state";
import {UmbrellaValidators} from "../../../umbrella-common/validation/umbrella-validators";
import {distinctUntilChanged, takeWhile} from "rxjs/operators";
import {AppState} from "../../../store/state/app.state";
import {Store} from "@ngrx/store";
import {OrderActions} from "../../../store/actions/order.actions";
import {TranslateService} from "@ngx-translate/core";
import {StandaloneFormService} from "../../../umbrella-common/services/standalone-form.service";
import {BaseOrderFormService} from "../common/base-order-form.service";

@Injectable()
export class ContactPersonBillingFormService extends StandaloneFormService implements BaseOrderFormService {

	readonly FORM_CONTROL_ERROR_MSG_MAP: { [validationKey: string]: ValidationMsg[] } = {
		pattern: [{formControlName: 'phone', msg: 'Enter your phone number without a leading 0, special characters, or spaces (2-12 digits)'}],
		too_long_phone: [{formControlName: 'phone', msg: 'Too many characters'}],
		requiredLength: [{msg: 'Zip code must contain 5 digits'}]
	};

	constructor(private fb: FormBuilder, private store: Store<AppState>, translate: TranslateService) {
		super(translate);
	}

	protected buildFormFor(formKey: string, [order]: [Order]) {
		const billingContactPerson = order.billingInformations.billingContactPerson;
		const form = this.fb.group({
			firstName: this.fb.control(billingContactPerson ? billingContactPerson.firstName : '', [Validators.required, UmbrellaValidators.textLengthBetween(2, 100)]),
			lastName: this.fb.control(billingContactPerson ? billingContactPerson.lastName : '', [Validators.required, UmbrellaValidators.textLengthBetween(2, 100)]),
			jobTitle: this.fb.control(billingContactPerson ? billingContactPerson.jobTitle : '', [Validators.required, UmbrellaValidators.textLengthBetween(2, 100)]),
			countryCode: this.fb.control(billingContactPerson && !!billingContactPerson.countryCode ? billingContactPerson.countryCode : '+1', [Validators.required]),
			phone: this.fb.control(this.getPhone(order.billingInformations), [Validators.required, UmbrellaValidators.phoneNumber]),
			email: this.fb.control(billingContactPerson ? billingContactPerson.email : '', [Validators.required, UmbrellaValidators.email()]),
		});

		form.controls.countryCode.disable();

		this.initFormControls(form);
		return form;
	}

	getControlNamesInOrder(): string[] {
		return ['firstName', 'lastName', 'jobTitle', 'email', 'phone', 'countryCode'];
	}

	protected override getFormSpecificErrorMap(): { [p: string]: ValidationMsg[] } {
		return this.FORM_CONTROL_ERROR_MSG_MAP;
	}

	private initFormControls(form: FormGroup) {
		form.controls['firstName'].valueChanges.pipe(takeWhile(() => this.isFormInitialized()), distinctUntilChanged())
			.subscribe(firstName => this.store.dispatch(OrderActions.SET_BILLING_CONTACT_PERSON_FIRST_NAME({firstName})));

		form.controls['lastName'].valueChanges.pipe(takeWhile(() => this.isFormInitialized()), distinctUntilChanged())
			.subscribe(lastName => this.store.dispatch(OrderActions.SET_BILLING_CONTACT_PERSON_LAST_NAME({lastName})));

		form.controls['jobTitle'].valueChanges.pipe(takeWhile(() => this.isFormInitialized()), distinctUntilChanged())
			.subscribe(jobTitle => this.store.dispatch(OrderActions.SET_BILLING_CONTACT_PERSON_JOB_TITLE({jobTitle})));

		form.controls['countryCode'].valueChanges.pipe(takeWhile(() => this.isFormInitialized()), distinctUntilChanged())
			.subscribe(countryCode => this.store.dispatch(OrderActions.SET_BILLING_COUNTRY_CODE({countryCode})));

		form.controls['phone'].valueChanges.pipe(takeWhile(() => this.isFormInitialized()), distinctUntilChanged())
			.subscribe(phoneNumber => this.store.dispatch(OrderActions.SET_BILLING_PHONE_NUMBER({phoneNumber: phoneNumber == null ? null : (phoneNumber as number).toString()})));

		form.controls['email'].valueChanges.pipe(takeWhile(() => this.isFormInitialized()), distinctUntilChanged())
			.subscribe(email => this.store.dispatch(OrderActions.SET_BILLING_CLIENT_EMAIL({email: this.toLowerCase(email)})));
	}

	private toLowerCase(value: string): string {
		return value != null ? value.toLowerCase() : value;
	}

	private getPhone(billingInformation: BillingInformation): string {
		if (!billingInformation.billingContactPerson) {
			return '';
		}
		const phone: string = billingInformation.billingContactPerson.phoneNumber;
		return phone != null ? phone.replace('+1', '') : null;
	}

	getContactPerson(): ContactPerson {
		return {
			firstName: this.getValueFor('firstName', StandaloneFormService.FORM_KEY),
			lastName: this.getValueFor('lastName', StandaloneFormService.FORM_KEY),
			jobTitle: this.getValueFor('jobTitle', StandaloneFormService.FORM_KEY),
			countryCode: this.getValueFor('countryCode', StandaloneFormService.FORM_KEY),
			phoneNumber: (this.getValueFor('phone', StandaloneFormService.FORM_KEY) as number).toString(),
			email: this.getValueFor('email', StandaloneFormService.FORM_KEY)
		};
	}

	fillOrder(order: Order): Order {
		order.billingInformations.billingContactPerson = this.getContactPerson();
		return order;
	}
}
